import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { addDistributorWhitelistEntry } from "helpers/backend-helper"

const TYPE_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "domain", label: "Domain" },
]

const AddDistributorWhitelistModal = ({ isOpen, onClose, onSuccess }) => {
  const [type, setType] = useState("email")
  const [value, setValue] = useState("")
  const [note, setNote] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!isOpen) return
    setType("email")
    setValue("")
    setNote("")
    setError("")
    setLoading(false)
  }, [isOpen])

  const handleClose = () => {
    if (loading) return
    onClose?.()
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      setError("Value is required")
      return
    }

    setLoading(true)
    setError("")

    try {
      const res = await addDistributorWhitelistEntry({
        type,
        value: trimmedValue,
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      onSuccess?.(res?.data?.promotedCount)
      onClose?.()
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={loading ? undefined : handleClose}
      backdrop="static"
      modalClassName="modal-top"
      keyboard={!loading}
    >
      <ModalHeader toggle={loading ? undefined : handleClose} className="text-black">
        Add to distributor whitelist
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {!!error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <FormGroup>
            <Label for="whitelist-type">Type</Label>
            <Input
              id="whitelist-type"
              type="select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={loading}
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="whitelist-value">
              {type === "domain" ? "Domain" : "Email"}
            </Label>
            <Input
              id="whitelist-value"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === "domain" ? "example.com" : "user@example.com"}
              disabled={loading}
              autoFocus
            />
          </FormGroup>

          <FormGroup>
            <Label for="whitelist-note">Note (optional)</Label>
            <Input
              id="whitelist-note"
              type="textarea"
              rows="3"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for whitelisting"
              disabled={loading}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" className="add-new-user" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding…
              </>
            ) : (
              "Add to whitelist"
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

AddDistributorWhitelistModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

export default AddDistributorWhitelistModal
